import React, { useEffect, useState } from 'react'
import {
  BuildingLibraryIcon,
  BookOpenIcon,
  GlobeEuropeAfricaIcon,
  AcademicCapIcon
} from '@heroicons/react/24/outline'
import { programService } from '../services/api'
import { PublicProgramDetail } from '../types'

const iconPalette = [BookOpenIcon, BuildingLibraryIcon, GlobeEuropeAfricaIcon, AcademicCapIcon]

const ProgramShowcase: React.FC = () => {
  const [programs, setPrograms] = useState<PublicProgramDetail[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    const fetchPrograms = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await programService.getPublicProgramDetails()
        if (isMounted) {
          setPrograms(data)
        }
      } catch (err) {
        console.error('Failed to load programs for showcase', err)
        if (isMounted) {
          setError('Unable to load programs right now. Please try again later.')
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }
    fetchPrograms()
    return () => {
      isMounted = false
    }
  }, [])

  const renderHighlights = (program: PublicProgramDetail) => {
    if (program.highlights && program.highlights.length > 0) {
      return program.highlights
    }
    if (program.description) {
      return [program.description]
    }
    return ['Details coming soon.']
  }

  const formatWeeks = (days?: number | null) => {
    if (!days) return null
    if (days % 7 === 0) {
      const weeks = days / 7
      return `${weeks} week${weeks === 1 ? '' : 's'}`
    }
    const weeks = Math.round((days / 7) * 10) / 10
    return `${weeks} weeks`
  }

  const formatDays = (days?: number | null) => {
    if (!days) return null
    return `${days} day${days === 1 ? '' : 's'}`
  }

  const formatPrice = (value?: number | null) => {
    if (value == null) return null
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value)
  }

  const formatDateRange = (start?: string | null, end?: string | null) => {
    if (!start && !end) return null
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    const startLabel = start ? formatter.format(new Date(start)) : 'TBD'
    const endLabel = end ? formatter.format(new Date(end)) : 'TBD'
    return `${startLabel} - ${endLabel}`
  }

  const computeDurationDays = (start?: string | null, end?: string | null) => {
    if (!start || !end) return null
    const startDate = new Date(start)
    const endDate = new Date(end)
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null
    const diffMs = endDate.getTime() - startDate.getTime()
    if (diffMs < 0) return null
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1
    return days > 0 ? days : null
  }

  const getPathwayDurationDays = (pathway: { duration_days?: number | null; start_date?: string | null; end_date?: string | null }) => (
    pathway.duration_days ?? computeDurationDays(pathway.start_date, pathway.end_date)
  )

  const getPathwayCapacity = (pathway: { capacity?: number | null }) => (
    typeof pathway.capacity === 'number' && pathway.capacity > 0 ? pathway.capacity : null
  )

  const splitPathways = (program: PublicProgramDetail) => {
    const coreTracks = program.cohorts ?? program.pathways ?? []
    const addons = program.extensions ?? []
    return { coreTracks, addons }
  }

  const sortByStartDate = <T extends { start_date?: string | null }>(items: T[]) => {
    const withKey = items.map((item, index) => ({
      item,
      index,
      time: item.start_date ? new Date(item.start_date).getTime() : Number.POSITIVE_INFINITY,
    }))
    return withKey
      .sort((a, b) => (a.time - b.time) || (a.index - b.index))
      .map((entry) => entry.item)
  }

  return (
    <section id="programs" className="py-24 px-6 min-h-screen">
      <div className="max-w-5xl mx-auto w-full">
        <p className="text-xs uppercase tracking-[0.5em] text-emerald-300 text-center">Available Programs</p>
        <h2 className="text-white text-3xl text-center font-semibold mt-3 mb-10">Where we can place you next</h2>

        {loading && (
          <div className="text-center text-white/80 py-12">Loading programs...</div>
        )}

        {error && (
          <div className="text-center text-red-200 bg-red-900/30 border border-red-500/40 rounded-xl p-4 mb-8">
            {error}
          </div>
        )}

        {!loading && !error && programs.length === 0 && (
          <div className="text-center text-white/80 py-12">
            New cohorts are being finalized. Share your info above and we’ll notify you as soon as seats open.
          </div>
        )}

        <div className={programs.length === 1 ? 'flex justify-center' : 'grid grid-cols-1 md:grid-cols-3 gap-6'}>
          {programs.map((program, idx) => {
            const Icon = iconPalette[idx % iconPalette.length]
            const highlights = renderHighlights(program)
            const { coreTracks, addons } = splitPathways(program)
            const sortedCoreTracks = sortByStartDate(coreTracks)
            const sortedAddons = sortByStartDate(addons)
            const totalCoreDays = coreTracks.reduce((sum, pathway) => sum + (getPathwayDurationDays(pathway) || 0), 0)
            const minCoreDays = coreTracks.reduce((min, pathway) => {
              const duration = getPathwayDurationDays(pathway) || 0
              if (!duration) return min
              return min === 0 ? duration : Math.min(min, duration)
            }, 0)
            const addonDays = addons.reduce((sum, pathway) => sum + (getPathwayDurationDays(pathway) || 0), 0)
            const maxTotalDays = totalCoreDays + addonDays
            const perCohortCapacity = program.cohort_capacity ?? program.seats_available ?? null
            const cohortCount = coreTracks.length || 0
            const cohortCapacities = coreTracks
              .map((track) => getPathwayCapacity(track))
              .filter((value): value is number => typeof value === 'number')
            const totalCapacityFromCohorts = cohortCapacities.length
              ? cohortCapacities.reduce((sum, value) => sum + value, 0)
              : null
            const totalCapacity = totalCapacityFromCohorts ?? (perCohortCapacity && cohortCount > 1
              ? perCohortCapacity * cohortCount
              : perCohortCapacity)
            const startingPrice = sortedCoreTracks[0]?.selling_price ?? null
            return (
              <div
                key={program.id}
                className="bg-white/10 backdrop-blur-md border border-white/15 rounded-3xl p-6 flex flex-col gap-4 w-full max-w-2xl"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-emerald-200">
                      {program.location || 'Location TBA'}
                    </p>
                    <h3 className="text-white text-xl font-semibold mt-1">{program.title}</h3>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
                    <Icon className="w-6 h-6 text-emerald-200" />
                  </div>
                </div>
                {program.description && (
                  <p className="text-white/85 text-sm">{program.description}</p>
                )}
                <ul className="space-y-2 text-white/80 text-sm">
                  {highlights.map((point, pointIdx) => (
                    <li key={pointIdx} className="flex items-start gap-2">
                      <span className="text-emerald-300 mt-0.5">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                {minCoreDays > 0 && (
                  <div className="text-white/70 text-xs">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <span className="uppercase tracking-[0.2em] text-emerald-200">Estimated Duration</span>
                        <div className="mt-1">
                          {formatWeeks(minCoreDays)} - {formatWeeks(maxTotalDays)}
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="uppercase tracking-[0.2em] text-emerald-200">Starting Price</span>
                        <div className="mt-1">
                          {formatPrice(startingPrice) || 'TBD'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {coreTracks.length > 0 && (
                  <div className="border-t border-white/10 pt-4 text-white/80 text-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-emerald-200 text-xs uppercase tracking-[0.3em]">Cohorts</p>
                        <p className="text-white/60 text-xs mt-1">Select one or both in the application.</p>
                        <p className="text-white/40 text-[11px] mt-1">Dates are subject to change.</p>
                      </div>
                      <span className="text-[11px] text-emerald-200/80 bg-emerald-500/10 border border-emerald-400/30 rounded-full px-2 py-0.5">
                        Core
                      </span>
                    </div>
                    <div
                      className={
                        coreTracks.length === 2
                          ? 'mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4'
                          : 'mt-3 space-y-3'
                      }
                    >
                      {sortedCoreTracks.map((track) => (
                        <div
                          key={track.id}
                          className="rounded-2xl border border-white/10 bg-white/5 p-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-white font-medium">{track.name}</span>
                            <span className="text-emerald-200 text-[11px]">
                              {formatWeeks(getPathwayDurationDays(track)) || 'Duration TBD'}
                            </span>
                          </div>
                          {getPathwayCapacity(track) && (
                            <p className="text-white/60 text-xs mt-1">Capacity: {getPathwayCapacity(track)}</p>
                          )}
                          {track.description && (
                            <p className="text-white/60 text-xs mt-1">{track.description}</p>
                          )}
                          <p className="text-white/50 text-xs mt-2">
                            {formatDateRange(track.start_date, track.end_date) || 'Dates TBD'}
                          </p>
                        </div>
                      ))}
                    </div>
                    {coreTracks.length > 1 && totalCoreDays > 0 && (
                      <p className="text-white/60 text-xs mt-3">
                        Choosing both core tracks ≈ {formatWeeks(totalCoreDays)} (before add-ons).
                      </p>
                    )}
                  </div>
                )}
                {addons.length > 0 && (
                  <div className="border-t border-white/10 pt-4 text-white/80 text-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-emerald-200 text-xs uppercase tracking-[0.3em]">Optional Extensions</p>
                        <p className="text-white/60 text-xs mt-1">Enhance your experience (not required).</p>
                        <p className="text-white/40 text-[11px] mt-1">Dates are subject to change.</p>
                      </div>
                      <span className="text-[11px] text-blue-200/90 bg-blue-500/10 border border-blue-400/30 rounded-full px-2 py-0.5">
                        Add-ons
                      </span>
                    </div>
                    <div className={addons.length === 2 ? 'mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4' : 'mt-3 space-y-3'}>
                      {sortedAddons.map((addon) => (
                        <div
                          key={addon.id}
                          className="rounded-2xl border border-white/10 bg-white/5 p-3"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <span className="text-white font-medium">{addon.name}</span>
                        <span className="text-blue-200 text-[11px]">
                          {formatDays(getPathwayDurationDays(addon)) ? `+${formatDays(getPathwayDurationDays(addon))}` : 'Duration TBD'}
                        </span>
                          </div>
                          {addon.description && (
                            <p className="text-white/60 text-xs mt-1">{addon.description}</p>
                          )}
                          <p className="text-white/50 text-xs mt-2">
                            {formatDateRange(addon.start_date, addon.end_date) || 'Dates TBD'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="mt-auto text-white/70 text-xs">
                  {totalCapacity ? (
                    <>Total capacity: {totalCapacity}</>
                  ) : cohortCount > 0 ? (
                    <>Total capacity: TBD</>
                  ) : (
                    <>Total capacity: TBD</>
                  )}
                </div>
                <p className="text-white/50 text-[11px]">
                  Pathway selection happens after you apply.
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}

export default ProgramShowcase
